import { installFreshHistoryAfterWrites } from '@/utils/freshHistoryAfterWrites';
import {
  installRefreshAfterHistoryVisit,
  type RestoredPage,
} from '@/utils/refreshAfterHistoryVisit';
import { sentryAllowUrls } from '@/utils/sentryAllowUrls';
import { installTolerantInsertBefore } from '@/utils/tolerantInsertBefore';
import { identifyClarityUser } from '@/utils/useClarityIdentify';
import { useHttp } from '@/utils/useHttp';
import { track } from '@/utils/useTracking';
import { createInertiaApp, router, usePage } from '@inertiajs/vue3';
import * as Sentry from '@sentry/vue';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import { ConfigProvider } from 'reka-ui';
import type { DefineComponent } from 'vue';
import { createSSRApp, h } from 'vue';
import { Ziggy } from './ziggy';

// `route()` is imported directly from `ziggy-js` wherever it's used, rather than
// injected as a Vue global, so Ziggy reads its route table off `globalThis`.
globalThis.Ziggy = Ziggy;

// Installed before the app mounts so it covers the very first patch. See the
// module for why a translated page otherwise kills the render tree.
installTolerantInsertBefore();

// Reload once when a lazily-imported page chunk fails to load. This happens when
// a tab stays open across a deploy and tries to fetch a hashed chunk whose
// filename no longer exists. The timestamp guard prevents a reload loop if the
// fresh build still can't resolve the chunk.
if (typeof window !== 'undefined') {
  window.addEventListener('vite:preloadError', event => {
    event.preventDefault();

    const key = 'vite:preloadError:at';
    const last = Number(sessionStorage.getItem(key) ?? 0);

    if (Date.now() - last < 10_000) {
      return;
    }

    sessionStorage.setItem(key, String(Date.now()));
    window.location.reload();
  });
}

// Back/forward after a write in this tab fetches the page fresh instead of
// restoring a stale copy from history. See the module.
installFreshHistoryAfterWrites(useHttp(), router);

// Back/forward shows Inertia's saved copy of a page, then refreshes it from the
// server. Registered before the app mounts so it sees the first page's navigate.
// See the module.
installRefreshAfterHistoryVisit(router, () => usePage() as unknown as RestoredPage);

// Tell Clarity which account a session belongs to. Registered before
// `createInertiaApp` so it also catches Inertia's initial-page `navigate` fire,
// and on every visit because the Clarity snippet itself only runs once per full
// page load — without this the identity would never follow an SPA navigation.
router.on('navigate', event => {
  const { page } = event.detail;

  identifyClarityUser(page.props.auth?.user, page.component);

  // Fired here rather than from a page component because the two registration
  // paths land in different places — OAuth on the dashboard, email/password on
  // verify-email. `justRegistered` is a one-shot server flash, so this runs once.
  if (page.props.justRegistered) {
    track('user.registered');
  }
});

// The Content-Security-Policy nonce, for the <style> elements libraries add at
// runtime: Inertia's progress bar, and Reka's (the Select viewport hides its
// scrollbar with one, DNDPLAYBOOK-11X).
const cspNonce = document.querySelector<HTMLMetaElement>('meta[property="csp-nonce"]')?.nonce;

createInertiaApp({
  title: title => (title ? `${title} | DndPlaybook` : 'DndPlaybook'),
  nonce: cspNonce,
  resolve: name =>
    resolvePageComponent(
      `./Pages/${name}.vue`,
      import.meta.glob<DefineComponent>('./Pages/**/*.vue')
    ),
  setup({ el, App, props, plugin }) {
    // ConfigProvider renders its slot as a fragment, so ssr.ts wraps the app in
    // it too (without a nonce, which the server has none of) or hydration fails.
    const app = createSSRApp({
      render: () => h(ConfigProvider, { nonce: cspNonce }, () => h(App, props)),
    }).use(plugin);

    Sentry.init({
      app,
      dsn: 'https://5df65f7ba5dac377f70985f9dba19d5d@o208557.ingest.us.sentry.io/4506559001657344',
      enabled: import.meta.env.PROD,

      ignoreErrors: [
        // Benign Inertia v3 infinite-scroll race: the IntersectionObserver
        // callback fires after the user has navigated away from the list page,
        // so the scroll prop is no longer on the current page object.
        /does not contain a scroll prop named/,
      ],

      // See sentryAllowUrls.ts — drops errors thrown by browser-injected code.
      allowUrls: sentryAllowUrls,
    });

    app.mount(el);

    return app;
  },
  progress: {
    color: '#4B5563',
  },
});
